import { __values } from 'tslib';
import { LocalStorageService } from '../services/local-storage.service';
import { customerType } from '../enums';
import { JD_ORDER_TABS, isJdSeller } from '../utils/jd-seller';

const storage = new LocalStorageService();
const getCurrentUsers = () => ({
    userData: storage.get('userData'),
    isDpdLogistic: storage.get('isDpdLogistic'),
    enableHoldPotFeature: storage.get('enableHoldPotFeature'),
    isAsendiaUser: storage.get('isAsendiaUser'),
    isAsendiaUserUk: storage.get('isAsendiaUserUk'),
    isJdSeller: isJdSeller(storage)
});

let draftForProblemOrders = [];

export const getOrdersNavList = (
    orderType: string,
    shippingType?: string,
    shippingTypeId?: string,
    onlineOrderRelatesTo?: string,
    tempDPDCondition?: boolean,
    navType?: string,
    enable_draft_orders?: boolean
): any[] => {
    const users = getCurrentUsers();
    const baseRoute = (type: string, tab = 'all') =>
        `/order/${orderType}/${type}/${shippingTypeId || 'all'}/${tab}`;
    let baseMarketRoute = (tab = 'all') =>
        `/order/${orderType}/${onlineOrderRelatesTo}/${shippingType}/all/${tab}`;
    if (users.isAsendiaUserUk) {
        baseMarketRoute = (tab = 'new') =>
            `/order/${orderType}/${onlineOrderRelatesTo}/all/${tab}`;
    }
    const navListMap: Record<string, () => any[]> = {
        draft: () => getDraft(shippingType, baseRoute, users),
        confirmed: () =>
            getConfirmed(
                tempDPDCondition,
                navType,
                orderType,
                shippingType,
                baseRoute,
                users
            ),
        marketplaces: () =>
            getMarketplaces(
                tempDPDCondition,
                navType,
                baseMarketRoute,
                users,
                enable_draft_orders
            ),
        'return-orders': () => getReturnOrder(),
        'b2c-orders': () => getB2COrders(),
        parcel: () => getParcels(),
        unsync: () => getUnsync(),
        products: () => getProducts(),
        'action-req': () => getActionRequired(users)
    };

    return navListMap[orderType]?.() || [];
};

const getDraft = (
    shippingType,
    baseRoute: (shippingType, tab) => string,
    users
) => {
    if (users.isAsendiaUserUk) {
        draftForProblemOrders = [
            { name: 'Draft', route: baseRoute(shippingType, 'new') },
            { name: 'Problem Orders', route: '/order/problem-orders/online' }
        ];
        return draftForProblemOrders;
    } else {
        return [
            { name: 'All', route: baseRoute(shippingType, 'all') },
            { name: 'New', route: baseRoute(shippingType, 'new') }
        ];
    }
};

const getConfirmed = (
    tempDPDCondition,
    navType,
    orderType,
    shippingType,
    baseRoute: (shippingType, tab) => string,
    users
) => {
    if (tempDPDCondition && navType === 'status') {
        return [
            'All',
            'Pick',
            'Pack',
            'Ready To Ship',
            'Transit',
            'Delivered',
            'Cancelled'
        ].map((name) => ({
            name,
            route: `/order/${orderType}/${shippingType}/all/${name
                .toLowerCase()
                .replace(/\s/g, '_')}`
        }));
    }

    // JD seller: only the JD sub-tabs (the shipping-type sub-menus stay as they are)
    if (users.isJdSeller) {
        return JD_ORDER_TABS.map(tab => ({ name: tab.name, route: baseRoute(shippingType, tab.segment) }));
    }

    const tabs = users.isAsendiaUserUk
        ? ['New', 'Pending', 'In-Process', 'Completed']
        : ['All', 'New', 'In-Process', 'Completed', 'Delivered', 'Cancelled'];

    const list = tabs.map((tab) => ({
        name: tab === 'Pending' ? 'Problem' : tab,
        route: baseRoute(shippingType, tab.toLowerCase().replace(/\s/g, '-'))
    }));

    // Pending tab is not enabled for a Confirmed submodule tabs list
    // if (users.userData?.is_java_integrated) {
    //     list.splice(4, 0, {
    //         name: 'Pending',
    //         route: baseRoute(shippingType, 'pending')
    //     });
    // }

    return list;
};

const getMarketplaces = (
    tempDPDCondition,
    navType,
    baseMarketRoute: (tab) => string,
    users,
    enable_draft_orders
) => {
    // JD seller: only the JD sub-tabs
    if (users.isJdSeller && !(tempDPDCondition && navType === 'status')) {
        return JD_ORDER_TABS.map(tab => ({ name: tab.name, route: baseMarketRoute(tab.segment) }));
    }

    const tabs =
        tempDPDCondition && navType === 'status'
            ? [
                  'Pick',
                  'Pack',
                  'Ready To Ship',
                  'Shipped',
                  'Cancelled',
                  'Completed'
              ]
            : users.isAsendiaUserUk
            ? ['New', 'Pending', 'In-Process', 'Completed']
            : [
                  'All',
                  'New',
                  'In-Process',
                  'Completed',
                  'Delivered',
                  'Cancelled',
                  'RTS Failure'
              ];

    const list = tabs.map((tab) => ({
        name: users.isAsendiaUserUk && tab === 'Pending' ? 'Problem' : tab,
        route: baseMarketRoute(mapRoutes(tab, users))
    }));

    if (enable_draft_orders) {
        list.splice(list.length - 6, 0, {
            name: 'Draft',
            route: baseMarketRoute('draft')
        });
        const allTab = list.shift(); // Remove "All" from first position
        list.push(allTab); // Add "All" to last position
    }

    if (users.enableHoldPotFeature) {
        list.push({ name: 'Hold', route: baseMarketRoute('hold-pot') });
    }

    if (!users.isAsendiaUserUk && users.isAsendiaUser) {
        list.splice(list.length - 4, 0, {
            name: 'Confirmed',
            route: baseMarketRoute('confirmed')
        });
    }

    if (users.userData?.is_java_integrated) {
        if (users.isDpdLogistic) {
            list.splice(2, 0, {
                name: 'Pending',
                route: baseMarketRoute('pending')
            });
        } else {
            list.splice(4, 0, {
                name: 'Pending',
                route: baseMarketRoute('pending')
            });
        }
    }

    // if (isFulfilmentCustomer) {
    //     list.push({
    //         name: 'Unpaid',
    //         route: baseMarketRoute('unpaid')
    //     });
    // }

    // console.log("list", list);
    return list;
};

const mapRoutes = (tab, users) => {
    if (users.isDpdLogistic) {
        switch (tab) {
            case 'Pick':
                tab = 'Created';
                break;
            case 'Pack':
                tab = 'Picked';
                break;
            case 'Shipped':
                tab = 'transit';
                break;
        }
    }

    return tab === 'RTS Failure'
        ? 'rts-failure'
        : tab.toLowerCase().replace(/\s/g, '_');
};

const getReturnOrder = () => {
    return [
        'New',
        'Approved',
        'In-Process',
        'Completed',
        'Cancelled',
        'Rejected'
    ].map((name) => ({ name, route: `/order/return/${name.toLowerCase()}` }));
};

const getB2COrders = () => {
    const users = getCurrentUsers();
    const list = ['All', 'New', 'In-Process', 'Completed', 'Cancelled'].map(
        (name) => ({
            name,
            route: `/order/b2c-orders/${name.toLowerCase().replace(/\s/g, '-')}`
        })
    );
    if (users.enableHoldPotFeature) {
        list.push({ name: 'Hold Pot', route: '/order/b2c-orders/hold-pot' });
    }
    return list;
};

const getParcels = () => {
    return [
        { name: 'All', route: '/order/parcel/all' },
        { name: 'Parcels In Transit', route: '/order/parcel/on-time' },
        { name: 'Delay', route: '/order/parcel/delay' },
        { name: 'Parcels Completed', route: '/order/parcel/delivered' }
    ];
};

const getUnsync = () => {
    return [{ name: 'All', route: '/order/unsync/all' }];
};

const getProducts = () => {
    // console.log("getProducts");
    return [
        { name: 'Channel Products', route: '/products/unsync/all' },
        { name: 'Inventory Products', route: '/products/unsync/offline' }
    ];
};

const getActionRequired = (users) => {
    return !users.isAsendiaUserUk
        ? users.isAsendiaUser
            ? [
                  { name: 'Online', route: '/order/problem-orders/online' },
                  { name: 'Offline', route: '/order/problem-orders/offline' }
              ]
            : [
                  { name: 'Online', route: '/order/problem-orders/online' },
                  { name: 'Offline', route: '/order/problem-orders/offline' },
                  // no Unsync with WMS tab for a JD seller
                  ...(users.isJdSeller
                      ? []
                      : [
                            {
                                name: 'Unsync with WMS',
                                route: '/order/problem-orders/unsync'
                            }
                        ])
              ]
        : [...draftForProblemOrders];
};

export function gridColumns(indicesToFilter = [], mapper: any = {}) {
    const mapperFn = {
        service: mapper.service || 'carrier',
        information: mapper.information || 'confirm_message',
        type: mapper.type || 'product_type',
        problemType: mapper.problemCategory || 'problem-category',
        problemMap: mapper.problemMap || 'problem_state',
        problemMethod: mapper.problemMethod || 'parseCommonAction'
    };
    const users = getCurrentUsers();
    const cols = columnsArray(users, mapperFn);

    return indicesToFilter.map((index) => cols[index]);
}

const columnsArray = (users, mapperFn) => {
    return [
        {
            index: 0,
            name: 'Order Number',
            isChecked: true,
            map: 'order_number',
            hasRedirect: true,
            redirectTo: 'order-view',
            method: 'parseCheckNull',
            sort: users?.isDpdLogistic
        },

        {
            index: 1,
            name: 'Ordered On',
            isChecked: true,
            map: 'order_on',
            method: 'formatMomentDate',
            sort: true
        },
        {
            index: 2,
            name: 'Shipment Date',
            isChecked: true,
            map: 'invoice_date',
            method: 'formatMomentDate',
            sort: true
        },
        {
            index: 3,
            name: 'Type',
            isChecked: true,
            map: mapperFn?.type,
            method: 'parseCheckNull'
        },
        {
            index: 4,
            name: 'Country',
            isChecked: true,
            map: 'destination_country',
            method: 'parseCheckNull'
        },
        {
            index: 5,
            name: users?.isDpdLogistic ? 'No of Products' : 'Product Units',
            isChecked: true,
            map: 'product_units',
            method: 'parseOrderPdtUnits'
        },
        {
            index: 6,
            name: 'No of Packages',
            isChecked: true,
            map: 'package',
            parseComponent: 'parsepackage'
        },
        {
            index: 7,
            name: 'Weight',
            isChecked: true,
            map: 'total_weight',
            method: 'parseCheckNull',
            parseComponent: users?.isDpdLogistic ? 'parseDeafultWeight' : ''
        },
        {
            index: 8,
            name: users?.isDpdLogistic
                ? 'Product'
                : !users?.isAsendiaUser
                ? 'Order Type'
                : 'Order/Service Type',
            isChecked: true,
            map: 'service_type',
            method:
                users?.isDpdLogistic || !users?.isAsendiaUser
                    ? 'parseNameModify'
                    : 'parseCheckNull'
        },
        {
            index: 9,
            name: 'Service',
            isChecked: true,
            map: mapperFn?.service,
            method: 'parseService'
        },
        {
            index: 10,
            name: 'Information',
            isChecked: true,
            map: mapperFn?.information,
            method: 'parseCheckNull'
        },
        {
            index: 11,
            name: 'Tracking',
            isChecked: true,
            map: 'tracking_number',
            method: 'parseCheckNull'
        },
        {
            index: 12,
            name: 'Print Status',
            isChecked: true,
            map: 'is_shipping_label_downloaded',
            method: 'parsePrintStatus',
            hasRedirect: true,
            redirectTo: 'print_status'
        },
        {
            index: 13,
            name: 'Marketplace',
            isChecked: true,
            map: 'source',
            method: 'parseCheckNull',
            sort: true
        },
        {
            index: 14,
            name: users?.isDpdLogistic ? 'Marketplace Service' : 'Carrier',
            isChecked: true,
            map: 'shipping_method',
            method: users?.isDpdLogistic
                ? 'parseCheckNull'
                : 'parseMultiWarehouseCarrier',
            sort: true
        },
        {
            index: 15,
            name: 'Actions',
            isChecked: true,
            map: 'allOrders',
            method: 'parseAction'
        },
        {
            index: 16,
            name: 'Primary Action 1',
            isChecked: true,
            map: 'allOrders',
            method: 'parseDpdStoreService'
        },
        {
            index: 17,
            name: 'Secondary Action',
            isChecked: true,
            map: 'allOrders',
            method: 'parseCancelAction'
        },

        // Non Dpd Grid Columns in MP orders Module
        {
            index: 18,
            name: 'Consignee',
            isChecked: true,
            map: 'consignee',
            method: 'parseCheckNull'
        },
        {
            index: 19,
            name: 'Sub Status',
            isChecked: true,
            map: 'substatus',
            method: 'parseCheckNull'
        },
        {
            index: 20,
            name: 'Type',
            isChecked: true,
            map: 'order_type',
            method: 'parseCheckNull'
        },
        {
            index: 21,
            name: 'Last Updated at',
            isChecked: true,
            map: 'updated_on',
            method: 'formatMomentDate'
        },
        {
            index: 22,
            name: 'Primary Action 2',
            isChecked: true,
            map: 'allOrders',
            method: 'parseDpdMapService'
        },

        {
            index: 23,
            name: 'Postcode',
            isChecked: true,
            map: 'consignee_zip',
            method: 'parseCheckPostcode'
        },
        {
            index: 24,
            name: 'Organisation/Name',
            isChecked: true,
            map: 'consignee',
            method: 'parseCheckNull'
        },
        {
            index: 25,
            name: 'Failed RTS Message',
            isChecked: true,
            map: 'failed_rts_message',
            method: 'parseCheckNull'
        },
        {
            index: 26,
            name: 'Problem Category',
            isChecked: true,
            map: 'problem_state',
            method: 'parseCommonAction',
            type: 'problem-category'
        },
        {
            index: 27,
            name: 'Sync Status',
            isChecked: true,
            map: 'wms_sync_status',
            method: 'parseWMSStatus'
        },
        {
            index: 28,
            name: 'Warehouse',
            isChecked: true,
            map: 'warehouse_code',
            method: 'parseWarehouseCode'
        },
        {
            index: 29,
            name: 'Carrier',
            isChecked: true,
            map: 'carrier',
            method: 'parseCheckNull'
        },
        {
            index: 30,
            name: 'Source',
            isChecked: true,
            map: 'source',
            method: 'parseCheckNull',
            parseComponent: 'seller_marketplace'
        },
        {
            index: 31,
            name: 'Status',
            isChecked: true,
            map: 'state_txt',
            method: 'parseOrderStatus'
        },
        {
            index: 32,
            name: 'Remarks',
            isChecked: true,
            map: 'failed_sync_reason',
            method: 'parseCheckNull'
        },
        {
            index: 33,
            name: 'Dispatch Date & Time',
            isChecked: true,
            map: 'dispatch_dateytime',
            method: 'formatMomentDate'
        },
        {
            index: 34,
            name: 'Reason',
            isChecked: true,
            map: 'reason',
            method: 'parseCheckNull'
        },
        {
            index: 35,
            name: 'Problem Category',
            isChecked: true,
            map: mapperFn?.problemMap,
            method: 'parseCheckNull',
            type: mapperFn?.problemType
        },
        {
            index: 36,
            name: 'Problem Type',
            isChecked: true,
            map: 'problem_state',
            method: 'parseCheckProblemOrders'
        },
        {
            index: 37,
            name: 'Parcel Code',
            isChecked: true,
            map: 'parcel_code',
            method: 'parseCheckNull'
        },
        {
            index: 38,
            name: 'Cancelled on',
            isChecked: true,
            map: 'updated_on',
            method: 'formatMomentDate',
            sort: true
        },
        {
            index: 39,
            name: 'Created On', // on merging this had a index of 26 and not 39
            isChecked: true,
            map: 'created_on',
            method: 'formatMomentDate',
            sort: true
        },
        {
            index: 40,
            name: 'Consignee',
            isChecked: true,
            map: 'billing_consignee'
        }
    ];
};

export const orderStatusNos = (navType?: string) => {
    let value = 0;
    switch (navType) {
        case 'all': {
            value = 1;
            break;
        }
        case 'new': {
            value = 2;

            break;
        }
        case 'approved': {
            value = 3;

            break;
        }
        case 'in-process': {
            value = 4;

            break;
        }
        case 'completed': {
            value = 5;

            break;
        }
        case 'cancelled': {
            value = 6;

            break;
        }
        case 'problem-category': {
            value = 9;

            break;
        }
        case 'delivered': {
            value = 10;

            break;
        }
    }
    return value;
};
